import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Heart, PenLine, Bookmark, BookMarked, LineChart, Users, ArrowRight, Flame } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DASH } from "@/constants/testIds/app";
import api from "@/lib/api";

const quotes = [
  { text: "There is no friend as loyal as a book.", author: "Hemingway" },
  { text: "Reading is a discount ticket to everywhere.", author: "Mary Schmich" },
  { text: "We read to know we are not alone.", author: "C.S. Lewis" },
  { text: "A book must be the axe for the frozen sea within us.", author: "Kafka" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [streak, setStreak] = useState(null);
  const q = quotes[new Date().getDate() % quotes.length];
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/streak");
        setStreak(data);
      } catch {}
    })();
  }, []);

  return (
    <div data-testid={DASH.container} className="space-y-12">
      <section>
        <div className="label-caps mb-4">{greet}</div>
        <h1 data-testid={DASH.greeting} className="font-serif-display text-4xl sm:text-5xl text-[#2C2520] tracking-tight">
          {greet}, <span className="italic">{user?.name}</span>.
          <br />
          How are you feeling today?
        </h1>
        <p className="mt-5 text-[#2C2520]/70 max-w-xl text-base leading-relaxed">
          Take a small moment. Share a mood or open a page. Something gentle is waiting for you.
        </p>
        {streak && streak.total_days > 0 && (
          <div
            data-testid="dashboard-streak"
            className="mt-6 inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-[#F5F2EA] border border-[#E8E3D5]"
          >
            <Flame
              className={streak.today ? "w-4 h-4 text-[#4A5D4E]" : "w-4 h-4 text-[#2C2520]/40"}
              strokeWidth={1.5}
            />
            <span className="text-sm text-[#2C2520]">
              {streak.current > 0 ? (
                <>
                  <span className="font-medium">{streak.current}-day</span>
                  <span className="text-[#2C2520]/70"> {streak.current === 1 ? "check-in" : "streak"}</span>
                  {streak.today ? (
                    <span className="text-[#4A5D4E] ml-2">· today ✓</span>
                  ) : (
                    <span className="text-[#2C2520]/60 ml-2">· continue today</span>
                  )}
                </>
              ) : (
                <span className="text-[#2C2520]/70">Longest streak: {streak.longest} day{streak.longest === 1 ? "" : "s"} · begin again today</span>
              )}
            </span>
          </div>
        )}
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <Link
          to="/mood"
          data-testid={DASH.moodCta}
          className="group bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-8 hover:shadow-[0_8px_24px_rgba(44,37,32,0.08)] transition-shadow duration-300"
        >
          <div className="w-11 h-11 rounded-[10px] bg-[#4A5D4E] flex items-center justify-center mb-5">
            <Heart className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="font-serif-display text-2xl text-[#2C2520]">Mood check-in</h2>
          <p className="text-[#2C2520]/70 mt-2 leading-relaxed text-sm">
            Name what you're feeling. A single word and a scale, that's all.
          </p>
          <div className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#4A5D4E] group-hover:gap-2.5 transition-all">
            Begin <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </div>
        </Link>

        <Link
          to="/journal"
          data-testid={DASH.journalCta}
          className="group bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-8 hover:shadow-[0_8px_24px_rgba(44,37,32,0.08)] transition-shadow duration-300"
        >
          <div className="w-11 h-11 rounded-[10px] bg-[#4A5D4E] flex items-center justify-center mb-5">
            <PenLine className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="font-serif-display text-2xl text-[#2C2520]">Journal entry</h2>
          <p className="text-[#2C2520]/70 mt-2 leading-relaxed text-sm">
            Write freely. Themes and tone will be gently inferred.
          </p>
          <div className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#4A5D4E] group-hover:gap-2.5 transition-all">
            Reflect <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </div>
        </Link>
      </section>

      <section className="grid md:grid-cols-3 gap-5">
        <QuickTile to="/library" testid={DASH.libraryCta} icon={Bookmark} title="Your library" subtitle="Saved books & practices" />
        <QuickTile to="/reading-list" testid="dashboard-reading-list-cta" icon={BookMarked} title="Reading list" subtitle="Books saved for later" />
        <QuickTile to="/timeline" testid={DASH.timelineCta} icon={LineChart} title="Mood timeline" subtitle="See patterns over time" />
      </section>

      <section className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-8 sm:p-10">
        <div className="label-caps mb-3">A quiet line</div>
        <p className="font-serif-display italic text-2xl sm:text-3xl text-[#2C2520] leading-snug">
          "{q.text}"
        </p>
        <p className="text-sm text-[#2C2520]/60 mt-3">{q.author}</p>
      </section>
    </div>
  );
}

function QuickTile({ to, testid, icon: Icon, title, subtitle }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="border border-[#E8E3D5] rounded-[12px] p-6 flex items-start gap-4 hover:bg-[#F5F2EA] transition-colors"
    >
      <div className="w-10 h-10 rounded-[10px] bg-[#FDFBF7] border border-[#E8E3D5] flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#4A5D4E]" strokeWidth={1.5} />
      </div>
      <div>
        <div className="font-serif-display text-lg text-[#2C2520]">{title}</div>
        <div className="text-sm text-[#2C2520]/60 mt-0.5">{subtitle}</div>
      </div>
    </Link>
  );
}
